import { Link } from "react-router-dom";
import { api } from "../api.ts";
import { AlertBanner, EmptyState } from "../components/ui.tsx";
import { useAsync } from "../hooks.ts";
import { isActionable } from "../../shared/schedule.ts";

/** The house, by floor. Each room says only what it is owed. */
export function Rooms() {
  const { data, error, loading } = useAsync(() => api.house());

  if (error) return <AlertBanner>Couldn’t load the house. {error}</AlertBanner>;
  if (loading && !data) return <p className="muted">Loading…</p>;
  if (!data) return null;

  if (data.floors.length === 0) {
    return <EmptyState icon="🏚" title="No rooms yet" body="A parent can add floors and rooms in the admin page." />;
  }

  return (
    <div>
      <header className="head">
        <span className="eyebrow">Every room</span>
        <h1 className="head__title">The house</h1>
        <p className="head__note">
          {data.away
            ? `✈️ On ${data.trip?.name ?? "a trip"} — home rooms are paused until you're back.`
            : "Tap a room to see and tick everything it needs."}
        </p>
      </header>

      <SeasonalList floors={data.floors} />

      {data.floors.map((floor) => (
        <section key={floor.id}>
          <div className="section-head">
            <span className="eyebrow">{floor.name}</span>
          </div>
          {floor.rooms.length === 0 ? (
            <p className="muted">No rooms on this floor yet.</p>
          ) : (
            <div className="rooms">
              {floor.rooms.map((room) => (
                <Link key={room.id} to={`/room/${room.id}`} className="room">
                  <span className="room__emoji" aria-hidden="true">
                    {room.emoji}
                  </span>
                  <span className="room__name">{room.name}</span>
                  <span
                    className={`room__count${
                      room.overdue > 0 ? " room__count--overdue" : room.actionable > 0 ? " room__count--due" : ""
                    }`}
                  >
                    {room.zone === "home" && data.away
                      ? "Paused ✈️"
                      : room.zone === "travel" && !data.away
                        ? "Waits for a trip"
                        : room.overdue > 0
                          ? `${room.overdue} overdue`
                          : room.actionable > 0
                            ? `${room.actionable} waiting`
                            : "All clear"}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

/**
 * The long jobs, kept out of the way.
 *
 * Ninety-day work never earns its place on Today: it is mid-overdue for weeks
 * at a time, too quiet to be urgent and too loud to disappear. Here it is a
 * list you choose to look at on a free Saturday, which is when it actually
 * gets done.
 */
function SeasonalList({
  floors,
}: {
  floors: Array<{
    rooms: Array<{
      id: number;
      name: string;
      emoji: string;
      tasks: Array<{ id: number; title: string; freq: string; status: string; daysLate: number; paused: boolean }>;
    }>;
  }>;
}) {
  const jobs = floors
    .flatMap((floor) => floor.rooms)
    .flatMap((room) => room.tasks.filter((task) => task.freq === "seasonal").map((task) => ({ task, room })))
    .filter(({ task }) => !task.paused && isActionable(task.status as never));

  if (jobs.length === 0) return null;

  return (
    <section>
      <div className="section-head">
        <span className="eyebrow">Sometime this season · {jobs.length}</span>
      </div>
      <ul className="tags">
        {jobs.slice(0, 6).map(({ task, room }) => (
          <li key={task.id} className="tag tag--never">
            <div className="tag__body">
              <div className="tag__title">{task.title}</div>
              <div className="tag__meta">
                <Link to={`/room/${room.id}`}>
                  {room.emoji} {room.name}
                </Link>
              </div>
            </div>
          </li>
        ))}
      </ul>
      {jobs.length > 6 ? <p className="kiosk__hidden">and {jobs.length - 6} more, in their rooms</p> : null}
    </section>
  );
}
